import { createContext } from 'react';
import { rolesFromClaims, tenantSlugFromClaims, type JwtPayload } from '@/utils/jwt';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

/**
 * Whether a role set may ENTER the console (DNKADM-0359 — the customer/staff
 * split, dink-api HANDOFF §0/§2). The staff ladder is `STAFF < ADMIN < OWNER`,
 * plus the platform `SUPER_ADMIN` a support token carries; a customer
 * (`tenant_role: "USER"`) is not on it.
 *
 * Deliberately a SEPARATE question from {@link ADMIN_ROLES}: *may you enter* and
 * *may you configure* were one boolean until this spec, which is why a STAFF
 * token got a locked app. STAFF enters; STAFF never configures.
 */
const OPERATOR_ROLES = new Set(['OWNER', 'SUPER_ADMIN', 'ADMIN', 'STAFF']);

/**
 * Whether a role set grants the admin surface (OWNER, SUPER_ADMIN, or ADMIN).
 * UNCHANGED by DNKADM-0359 — STAFF is deliberately absent, so every existing
 * `isAdmin` consumer (`RequireAdmin`, `NavLeaf.gated`, `navAccessSummary`, the
 * per-screen write controls) keeps its exact meaning.
 */
const ADMIN_ROLES = new Set(['OWNER', 'SUPER_ADMIN', 'ADMIN']);

/**
 * The staff FLOOR — `tenant_role: "STAFF"` (DNKADM-0359). *Their own world, the
 * day's bookings; no money, no settings, no team.*
 */
const STAFF_TIER_ROLE = 'STAFF';

/**
 * The one `principal_type` claim VALUE that marks a tenant staff member —
 * STAFF, ADMIN **and** OWNER all carry it (saas-auth SASATH-0319, dink-api
 * HANDOFF §0). INFORMATIONAL ONLY: the tier comes from `tenant_role`, never
 * from this claim, so nothing may be AUTHORIZED on it — see
 * {@link CurrentOperator.isTenantStaff} for what that rule does and does not
 * cover (DNKADM-0368).
 */
const TENANT_STAFF_PRINCIPAL_TYPE = 'TENANT_STAFF';

/**
 * The one `principal_type` claim VALUE that marks a platform-support session
 * (DNKADM-0356; saas-auth SASATH-0313, contract 2026.09.01) — a platform
 * SUPER_ADMIN acting inside one tenant's apps with tenant-scoped claims and no
 * roster row, by construction.
 *
 * Matched by VALUE, never by the claim's presence: member and staff tokens carry
 * no `principal_type` at all, and the other platform principals carry other
 * values (`PLATFORM`, `PRODUCT_ADMIN`) which are NOT support sessions.
 */
const PLATFORM_SUPPORT_PRINCIPAL_TYPE = 'PLATFORM_SUPPORT';

/** The `tenant_role` tier that owns the tenant's Team & access (DNKADM-0483). */
const OWNER_ROLE = 'OWNER';

export interface CurrentOperator {
  email: string;
  name: string | undefined;
  tenantSlug: string | undefined;
  roles: string[];
  /**
   * True when the principal may ENTER the console: OWNER, SUPER_ADMIN, ADMIN or
   * STAFF (DNKADM-0359). This — not {@link isAdmin} — is the shell-level gate in
   * `AuthGate`; a customer (`tenant_role: "USER"`) still gets `NoConsoleAccess`.
   */
  isOperator: boolean;
  /**
   * True when the operator holds OWNER, SUPER_ADMIN, or ADMIN — may CONFIGURE.
   * Unchanged by DNKADM-0359: STAFF is **not** admin, and every surface that was
   * admin-only stays admin-only.
   */
  isAdmin: boolean;
  /**
   * True only for the staff FLOOR — the `tenant_role` claim is exactly `STAFF`
   * (read on its own, never off the merged role set) and the session holds no
   * admin role (DNKADM-0359). Drives what the SHELL renders (nav leaves, the notifications
   * bell, Front Desk availability, the empty-location state), never what the
   * server allows: authorization is dink-api's. Since DNKADM-0375 the rule is
   * RUN open, SET UP closed, and this app only stops rendering the doors dink-api
   * keeps OWNER/ADMIN.
   */
  isStaffTier: boolean;
  /**
   * True only when `principal_type` is exactly `TENANT_STAFF` (DNKADM-0359),
   * matched BY VALUE — absence is `false`.
   *
   * **Never AUTHORIZE on it.** OWNER and ADMIN carry the same claim, so it says
   * "this is a tenant staff member", not "this is the floor"; the tier is
   * {@link isStaffTier}, derived from `tenant_role`. That rule is about
   * authorization — what the SERVER allows — and it stands: no route gate, nav
   * gate, write control, or `403`-shaped decision may key on this claim.
   *
   * **It is not a ban on reading the claim at all.** DNKADM-0368 read it once,
   * temporarily, in {@link frontDeskUnavailable} — withholding a CLIENT-ONLY
   * presentation mode whose exit provably could not succeed was not
   * authorization, the same class of decision DNKADM-0356 (support) and
   * DNKADM-0359 (the STAFF floor) already made on the identical predicate.
   * DNKADM-0369 dropped that read once SASATH-0321 fixed the underlying
   * re-auth failure upstream (see {@link frontDeskUnavailable}'s doc). The
   * field stays on the operator, informational, should a future client-only
   * decision need the same read; the authorization rule above still governs it.
   */
  isTenantStaff: boolean;
  /**
   * True only when `principal_type` is exactly `PLATFORM_SUPPORT` (DNKADM-0356).
   *
   * Display + Front-Desk availability, plus the one term of {@link canManageTeam}
   * (DNKADM-0483). It gates no surface on its own: a support token holds
   * SUPER_ADMIN and passes exactly the gates a tenant SUPER_ADMIN passes
   * (`isAdmin` is unaffected), and authorization stays the server's.
   */
  isPlatformSupport: boolean;
  /**
   * True when the session may use the owner-only Team & access controls
   * (DNKADM-0483, mirroring dink-api DNKAPI-0698): the `tenant_role` claim is
   * `OWNER`, **or** it is a HUMAN platform support session —
   * {@link isPlatformSupport} and the token carries no `azp` claim. An
   * agent-shaped support token (`azp` present, e.g. `claude-ops`) is never
   * elevated; a tenant SUPER_ADMIN/ADMIN/STAFF login never is either.
   *
   * Presentation only: it decides which controls RENDER. dink-api enforces the
   * same rule on the six owner-only `platform-members` writes.
   */
  canManageTeam: boolean;
  /**
   * True when Front Desk mode must be withheld from this session — the ONE
   * definition of that predicate, read by both sites that act on it
   * (`FrontDeskContext`, which forces the mode off, and `AppLayout`, which does
   * not render the entry control). It lives here so the two can never drift
   * (DNKADM-0368).
   *
   * Two principals, one mechanism: the mode's EXIT re-authenticates the
   * admin's own password via `POST /me/verify-password`, which saas-auth
   * resolves through the tenant's membership row —
   * `InternalPasswordVerificationService:65`. A principal with no matching row
   * gets `valid:false` for the CORRECT password and the dialog locks the
   * console after five tries.
   *
   * - {@link isPlatformSupport} (DNKADM-0356) — no roster row, by construction.
   * - {@link isStaffTier} (DNKADM-0359) — already the restricted view, and the
   *   same re-auth; the re-auth reason is now moot for them too (see below),
   *   but the product reason is not — the floor has no admin leaves for the
   *   mode to hide, so withholding it stays a no-op.
   *
   * `isTenantStaff` is DELIBERATELY ABSENT (DNKADM-0369). DNKADM-0368 added it
   * TEMPORARILY: after the customer/staff split's cutover, saas-auth's internal
   * verify-password resolved callers through `tenant_users` only, so an OWNER
   * or ADMIN whose only row moved to `tenant_staff` hit the trap above
   * (dink-api's REPLY §c, verified), and the term withheld the mode from every
   * TENANT_STAFF session to avoid it. SASATH-0321 (`verify-password-staff-aware`)
   * fixed the resolution upstream once the Operator announced it on saas-auth's
   * staging — "member of this tenant" now means a `tenant_customers` row **or**
   * a `tenant_staff` row — so the trap is gone and OWNER/ADMIN staff logins
   * regain Front Desk mode. Do not re-add this term without the same
   * re-auth-failure argument DNKADM-0368 made; {@link isTenantStaff}'s own doc
   * carries the read/authorize distinction that still governs the claim.
   *
   * Presentation only, never authorization — it withholds a client-side mode; it
   * neither grants nor refuses anything the server decides.
   */
  frontDeskUnavailable: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  operator: CurrentOperator | null;
  /**
   * True after a runtime session-expiry (a bearer-surface `401` that could not be
   * silently refreshed -> `signOut({ expired: true })`). Drives the global
   * "Session expired" interstitial in `AuthGate`. A separate boolean, NOT a member
   * of `AuthStatus`, so the union's app-wide consumers are unaffected. Cleared on a
   * successful (re-)auth so it can never get stuck.
   */
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  setAuthed: (token: string, expiresAt: string | null) => void;
  setUnauthed: () => void;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function deriveOperator(claims: JwtPayload | null): CurrentOperator | null {
  if (!claims) return null;
  const roles = rolesFromClaims(claims);
  const isAdmin = roles.some((r) => ADMIN_ROLES.has(r));
  // The TIER claim, read on its own — NOT off the `roles` set above, which
  // `rolesFromClaims` builds by MERGING `roles[]` with `tenant_role`, so a
  // mixed token would misclassify. Normalized only for the `ROLE_` prefix that
  // `rolesFromClaims` also strips (dink-api prefixes this claim when it builds
  // authorities; saas-auth mints it bare).
  const tenantRole =
    typeof claims.tenant_role === 'string'
      ? claims.tenant_role.trim().replace(/^ROLE_/, '')
      : undefined;
  // The floor: the TIER claim is exactly STAFF **and** the session is not an
  // admin by any claim. The `!isAdmin` conjunct is a safety net, not a second
  // definition — it makes "floor" and "may configure" mutually exclusive, so
  // no session can ever be shown the staff shell while passing admin gates.
  const isStaffTier = tenantRole === STAFF_TIER_ROLE && !isAdmin;
  // Informational (see the field doc): OWNER/ADMIN carry TENANT_STAFF too.
  const isTenantStaff = claims.principal_type === TENANT_STAFF_PRINCIPAL_TYPE;
  // The ONLY place the claim is read — everything else consumes this flag.
  const isPlatformSupport = claims.principal_type === PLATFORM_SUPPORT_PRINCIPAL_TYPE;
  // A human support session: no `azp` at all (an agent token names its client
  // there). JSON cannot carry `undefined`, so `=== undefined` means absent; any
  // value, even an empty string, counts as present and fails closed.
  const canManageTeam =
    tenantRole === OWNER_ROLE || (isPlatformSupport && claims.azp === undefined);
  return {
    email: claims.email ?? claims.sub ?? '',
    name: claims.name,
    tenantSlug: tenantSlugFromClaims(claims),
    roles,
    // Reads the same merged role set `isAdmin` does, DELIBERATELY: this gate
    // replaced `isAdmin` in `AuthGate`, so it must admit every principal
    // `isAdmin` admitted, including the many tokens that carry `roles: ['OWNER']`
    // with no `tenant_role`. Reading the tier claim alone here would lock those
    // out — a regression, not a tightening. It cannot misclassify a customer:
    // `tenant_role: "USER"` / `roles: ['ROLE_USER']` intersects the set nowhere.
    isOperator: roles.some((r) => OPERATOR_ROLES.has(r)),
    isAdmin,
    isStaffTier,
    isTenantStaff,
    isPlatformSupport,
    canManageTeam,
    // The ONE definition of "withhold Front Desk mode" — both acting sites read
    // this flag rather than recomputing it (DNKADM-0368/0369; see the field doc
    // for why each surviving term is here — `isTenantStaff` is deliberately not
    // one of them any more).
    frontDeskUnavailable: isPlatformSupport || isStaffTier,
  };
}
